import { TopicGroup } from '../algebra1/lessons';

export const triangles: TopicGroup = {
  id: 'triangles',
  title: 'Triangles',
  icon: 'ri-shape-line',
  lessons: [
    {
      id: 'triangle-basics',
      title: 'Triangle Properties & Classification',
      description: 'Master triangle types, angle sums, and basic properties',
      textbookExplanation: `
# Triangle Fundamentals

A **triangle** is a polygon with three sides and three angles.

## Triangle Angle Sum Theorem:
The sum of the interior angles of any triangle is always **180°**.

## Classification by Sides:
- **Equilateral**: all three sides equal (all angles = 60°)
- **Isosceles**: two sides equal (two angles equal)
- **Scalene**: all sides different (all angles different)

## Classification by Angles:
- **Acute triangle**: all angles less than 90°
- **Right triangle**: one angle equals 90°
- **Obtuse triangle**: one angle greater than 90°

## Exterior Angle Theorem:
An exterior angle of a triangle equals the sum of the two non-adjacent interior angles.

## Triangle Inequality Theorem:
The sum of any two sides must be greater than the third side.
      `,
      shortMethod: `
# Mr. Augustine's Triangle Shortcuts

**The "180° Rule" (Most Important!):**
All three angles ALWAYS add to 180°. Know two angles? Subtract from 180° to get the third!

**The "Isosceles = Two Twins" Rule:**
If two sides are equal, the angles opposite them are equal too!

**The "Exterior Angle = Add the Far Two" Trick:**
Don't go around the triangle! Just add the two angles far from the exterior angle.

**The "Triangle Inequality Quick Check":**
Can you make a triangle with sides 3, 4, 5?
- Check: 3 + 4 = 7, which is greater than 5 ✓
- Check: 3 + 5 = 8, which is greater than 4 ✓
- Check: 4 + 5 = 9, which is greater than 3 ✓
All pass? It's a valid triangle!

**The "Equilateral = All 60°" Shortcut:**
If all sides equal, don't calculate—each angle is 60°!
      `,
      workedExamples: [
        {
          problem: 'Two angles of a triangle measure 48° and 73°. Find the third angle.',
          solution: `
**Step 1:** Use the angle sum theorem (sum = 180°)

**Step 2:** Subtract the known angles
Third angle = 180° - 48° - 73° = 59°

**Answer:** The third angle is 59°
          `
        },
        {
          problem: 'An isosceles triangle has a vertex angle of 40°. Find the base angles.',
          solution: `
**Step 1:** Let each base angle = x (they're equal in isosceles)

**Step 2:** Set up equation
40° + x + x = 180°
40° + 2x = 180°

**Step 3:** Solve
2x = 140°
x = 70°

**Answer:** Each base angle is 70°
          `
        },
        {
          problem: 'Can sides of length 5, 7, and 13 form a triangle?',
          solution: `
**Step 1:** Check triangle inequality (sum of two sides > third side)

**Step 2:** Test: 5 + 7 = 12, which is NOT greater than 13

**Step 3:** Since one test fails, these cannot form a triangle

**Answer:** No, these sides cannot form a triangle
          `
        }
      ],
      practiceQuestions: [
        {
          id: 1,
          question: 'Two angles of a triangle are 55° and 70°. Find the third angle.',
          options: ['45°', '55°', '65°', '75°'],
          correctAnswer: 1,
          solution: 'Third angle = 180° - 55° - 70° = 55°'
        },
        {
          id: 2,
          question: 'In an equilateral triangle, what is each angle?',
          options: ['45°', '60°', '90°', '120°'],
          correctAnswer: 1,
          solution: 'In an equilateral triangle, all angles equal 60°'
        },
        {
          id: 3,
          question: 'An isosceles triangle has base angles of 65° each. Find the vertex angle.',
          options: ['40°', '50°', '60°', '70°'],
          correctAnswer: 1,
          solution: 'Vertex = 180° - 65° - 65° = 50°'
        },
        {
          id: 4,
          question: 'Can sides 3, 4, and 8 form a triangle?',
          options: ['Yes', 'No', 'Only if right', 'Only if obtuse'],
          correctAnswer: 1,
          solution: '3 + 4 = 7, which is not greater than 8. Cannot form a triangle.'
        },
        {
          id: 5,
          question: 'A triangle has angles x, 2x, and 3x. Find x.',
          options: ['20°', '25°', '30°', '35°'],
          correctAnswer: 2,
          solution: 'x + 2x + 3x = 180°, so 6x = 180°, x = 30°'
        },
        {
          id: 6,
          question: 'An exterior angle measures 125°. If one remote interior is 60°, find the other.',
          options: ['55°', '60°', '65°', '70°'],
          correctAnswer: 2,
          solution: 'Exterior = sum of remote interiors. 125° - 60° = 65°'
        },
        {
          id: 7,
          question: 'A triangle has angles 45°, 45°, and 90°. What type is it?',
          options: ['Acute', 'Right', 'Obtuse', 'Equilateral'],
          correctAnswer: 1,
          solution: 'It has a 90° angle, so it is a right triangle'
        },
        {
          id: 8,
          question: 'Two angles are 38° and 104°. Find the third angle.',
          options: ['28°', '38°', '48°', '58°'],
          correctAnswer: 1,
          solution: '180° - 38° - 104° = 38°'
        },
        {
          id: 9,
          question: 'An isosceles triangle has vertex angle 100°. Find each base angle.',
          options: ['30°', '35°', '40°', '45°'],
          correctAnswer: 2,
          solution: '(180° - 100°) ÷ 2 = 80° ÷ 2 = 40°'
        },
        {
          id: 10,
          question: 'Can sides 5, 12, and 13 form a triangle?',
          options: ['Yes', 'No', 'Only right', 'Cannot tell'],
          correctAnswer: 0,
          solution: '5 + 12 = 17 > 13 ✓, 5 + 13 = 18 > 12 ✓, 12 + 13 = 25 > 5 ✓'
        },
        {
          id: 11,
          question: 'A triangle has angles 2x, 3x, and 4x. Find the largest angle.',
          options: ['60°', '70°', '80°', '90°'],
          correctAnswer: 2,
          solution: '2x + 3x + 4x = 180°, so 9x = 180°, x = 20°. Largest = 4(20°) = 80°'
        },
        {
          id: 12,
          question: 'An exterior angle is 140°. If remote interiors are equal, find each.',
          options: ['60°', '65°', '70°', '75°'],
          correctAnswer: 2,
          solution: 'Each remote interior = 140° ÷ 2 = 70°'
        },
        {
          id: 13,
          question: 'A triangle has all angles less than 90°. What type is it?',
          options: ['Acute', 'Right', 'Obtuse', 'Equilateral'],
          correctAnswer: 0,
          solution: 'All angles less than 90° means it is an acute triangle'
        },
        {
          id: 14,
          question: 'Two angles are 27° and 118°. Find the third.',
          options: ['25°', '30°', '35°', '40°'],
          correctAnswer: 2,
          solution: '180° - 27° - 118° = 35°'
        },
        {
          id: 15,
          question: 'An isosceles triangle has base angles x and vertex 2x. Find x.',
          options: ['40°', '45°', '50°', '55°'],
          correctAnswer: 1,
          solution: 'x + x + 2x = 180°, so 4x = 180°, x = 45°'
        },
        {
          id: 16,
          question: 'Can sides 7, 8, and 16 form a triangle?',
          options: ['Yes', 'No', 'Only acute', 'Only obtuse'],
          correctAnswer: 1,
          solution: '7 + 8 = 15, which is not greater than 16. Cannot form triangle.'
        },
        {
          id: 17,
          question: 'An exterior angle is 110°. One remote interior is 45°. Find the other.',
          options: ['55°', '60°', '65°', '70°'],
          correctAnswer: 2,
          solution: '110° - 45° = 65°'
        },
        {
          id: 18,
          question: 'A triangle has one angle of 120°. What type is it?',
          options: ['Acute', 'Right', 'Obtuse', 'Cannot tell'],
          correctAnswer: 2,
          solution: 'One angle greater than 90° means it is obtuse'
        },
        {
          id: 19,
          question: 'Two angles are 62° and 58°. Find the third.',
          options: ['50°', '55°', '60°', '65°'],
          correctAnswer: 2,
          solution: '180° - 62° - 58° = 60°'
        },
        {
          id: 20,
          question: 'An isosceles right triangle has how many 45° angles?',
          options: ['0', '1', '2', '3'],
          correctAnswer: 2,
          solution: 'One 90° angle and two equal 45° angles (90° + 45° + 45° = 180°)'
        }
      ]
    },
    {
      id: 'triangle-congruence',
      title: 'Triangle Congruence',
      description: 'Learn SSS, SAS, ASA, AAS, and HL congruence theorems',
      textbookExplanation: `
# Triangle Congruence

Two triangles are **congruent** if they have the same size and shape. All corresponding sides and angles are equal.

## Congruence Postulates (Ways to Prove Triangles Congruent):

**1. SSS (Side-Side-Side):**
If all three sides of one triangle equal the three sides of another, the triangles are congruent.

**2. SAS (Side-Angle-Side):**
If two sides and the included angle of one triangle equal two sides and the included angle of another, the triangles are congruent.

**3. ASA (Angle-Side-Angle):**
If two angles and the included side of one triangle equal two angles and the included side of another, the triangles are congruent.

**4. AAS (Angle-Angle-Side):**
If two angles and a non-included side of one triangle equal two angles and the corresponding non-included side of another, the triangles are congruent.

**5. HL (Hypotenuse-Leg) - Right Triangles Only:**
If the hypotenuse and one leg of a right triangle equal the hypotenuse and one leg of another right triangle, they are congruent.

## Important Notes:
- **AAA is NOT a congruence theorem** (triangles can be similar but different sizes)
- **SSA is NOT a congruence theorem** (ambiguous case)
- Order matters when writing congruence statements: △ABC ≅ △DEF means A↔D, B↔E, C↔F
      `,
      shortMethod: `
# Mr. Augustine's Congruence Shortcuts

**The "3 Things Rule":**
You need exactly 3 matching pieces to prove congruence (with the right combination).

**Memory Trick - "SAS is a Sandwich":**
The angle must be BETWEEN the two sides (like meat between bread).

**Memory Trick - "ASA is Also a Sandwich":**
The side must be BETWEEN the two angles.

**The "Right Triangle Special" (HL):**
Only works for right triangles! Match the hypotenuse + one leg = done!

**The "Two Angles = Third Angle" Shortcut:**
If you have two angles matching (ASA or AAS), the third angle automatically matches too (because angles sum to 180°).

**What DOESN'T Work:**
- AAA = Similar, not congruent (same shape, different size)
- SSA = The "Donkey Theorem" (unreliable!)

**Quick Decision Tree:**
1. All three sides? → SSS
2. Two sides with angle between? → SAS
3. Two angles with side between? → ASA
4. Two angles with side NOT between? → AAS
5. Right triangle with hypotenuse + leg? → HL
      `,
      workedExamples: [
        {
          problem: 'Triangle ABC has sides 5, 7, 9. Triangle DEF has sides 5, 7, 9. Are they congruent? Which theorem?',
          solution: `
**Step 1:** Identify what is given
All three sides match: 5 = 5, 7 = 7, 9 = 9

**Step 2:** Determine the theorem
This is SSS (Side-Side-Side)

**Answer:** Yes, congruent by SSS
          `
        },
        {
          problem: 'In triangles ABC and DEF: AB = DE, ∠A = ∠D, AC = DF. Are they congruent?',
          solution: `
**Step 1:** Identify the given information
Two sides (AB = DE, AC = DF) and the included angle (∠A = ∠D)

**Step 2:** Check if angle is between the sides
Yes, ∠A is between sides AB and AC

**Step 3:** Apply theorem
This is SAS (Side-Angle-Side)

**Answer:** Yes, congruent by SAS
          `
        },
        {
          problem: 'Two right triangles have hypotenuses of 13 and legs of 5. Are they congruent?',
          solution: `
**Step 1:** Identify triangle type
Both are right triangles

**Step 2:** Check given information
Hypotenuse = 13 (both)
One leg = 5 (both)

**Step 3:** Apply right triangle theorem
This is HL (Hypotenuse-Leg)

**Answer:** Yes, congruent by HL
          `
        }
      ],
      practiceQuestions: [
        {
          id: 1,
          question: 'Triangles have sides 6, 8, 10 and 6, 8, 10. Which theorem proves congruence?',
          options: ['SSS', 'SAS', 'ASA', 'AAS'],
          correctAnswer: 0,
          solution: 'All three sides match, so use SSS'
        },
        {
          id: 2,
          question: 'Two sides and the included angle match. Which theorem?',
          options: ['SSS', 'SAS', 'ASA', 'AAS'],
          correctAnswer: 1,
          solution: 'Two sides with angle between them is SAS'
        },
        {
          id: 3,
          question: 'Two angles and the included side match. Which theorem?',
          options: ['SSS', 'SAS', 'ASA', 'AAS'],
          correctAnswer: 2,
          solution: 'Two angles with side between them is ASA'
        },
        {
          id: 4,
          question: 'Can AAA prove triangle congruence?',
          options: ['Yes, always', 'No, never', 'Only for right triangles', 'Only for equilateral'],
          correctAnswer: 1,
          solution: 'AAA proves similarity, not congruence. Triangles can be different sizes.'
        },
        {
          id: 5,
          question: 'Right triangles: hypotenuse and one leg match. Which theorem?',
          options: ['SSS', 'SAS', 'HL', 'AAS'],
          correctAnswer: 2,
          solution: 'Hypotenuse-Leg (HL) is for right triangles only'
        },
        {
          id: 6,
          question: 'Two angles and a non-included side match. Which theorem?',
          options: ['SSS', 'SAS', 'ASA', 'AAS'],
          correctAnswer: 3,
          solution: 'Two angles with side NOT between them is AAS'
        },
        {
          id: 7,
          question: 'If △ABC ≅ △DEF, which angle corresponds to ∠B?',
          options: ['∠D', '∠E', '∠F', 'Cannot tell'],
          correctAnswer: 1,
          solution: 'In congruence statement, order matters: B corresponds to E'
        },
        {
          id: 8,
          question: 'Can SSA prove triangle congruence?',
          options: ['Yes, always', 'No, never', 'Only for right triangles', 'Only for obtuse'],
          correctAnswer: 1,
          solution: 'SSA is ambiguous and does NOT prove congruence'
        },
        {
          id: 9,
          question: 'Triangles have matching sides 4, 5 and included angle 60°. Which theorem?',
          options: ['SSS', 'SAS', 'ASA', 'Cannot prove'],
          correctAnswer: 1,
          solution: 'Two sides with included angle is SAS'
        },
        {
          id: 10,
          question: 'Triangles have angles 50°, 60° and included side 8. Which theorem?',
          options: ['SSS', 'SAS', 'ASA', 'AAS'],
          correctAnswer: 2,
          solution: 'Two angles with included side is ASA'
        },
        {
          id: 11,
          question: 'If △PQR ≅ △STU, which side corresponds to QR?',
          options: ['ST', 'TU', 'SU', 'Cannot tell'],
          correctAnswer: 1,
          solution: 'Q↔T and R↔U, so QR corresponds to TU'
        },
        {
          id: 12,
          question: 'Right triangles: legs 3 and 4 match. Which theorem?',
          options: ['SSS', 'SAS', 'HL', 'Need hypotenuse'],
          correctAnswer: 1,
          solution: 'Two legs with right angle between is SAS (or can use SSS if you find hypotenuse)'
        },
        {
          id: 13,
          question: 'Triangles have angles 40°, 70° and non-included side 10. Which theorem?',
          options: ['SSS', 'SAS', 'ASA', 'AAS'],
          correctAnswer: 3,
          solution: 'Two angles with non-included side is AAS'
        },
        {
          id: 14,
          question: 'Three angles match but no sides given. Are triangles congruent?',
          options: ['Yes, by AAA', 'Yes, by ASA', 'No, need side', 'Cannot tell'],
          correctAnswer: 2,
          solution: 'AAA only proves similarity, not congruence. Need at least one side.'
        },
        {
          id: 15,
          question: 'Triangles have sides 7, 9 and non-included angle 50°. Can you prove congruence?',
          options: ['Yes, by SAS', 'Yes, by SSA', 'No, SSA is ambiguous', 'Yes, by ASA'],
          correctAnswer: 2,
          solution: 'SSA (two sides and non-included angle) does not prove congruence'
        },
        {
          id: 16,
          question: 'If △ABC ≅ △XYZ, and AB = 12, what is XY?',
          options: ['6', '12', '24', 'Cannot tell'],
          correctAnswer: 1,
          solution: 'Corresponding sides are equal. AB corresponds to XY, so XY = 12'
        },
        {
          id: 17,
          question: 'Right triangles: hypotenuse 10, leg 6 match. Which theorem?',
          options: ['SSS', 'SAS', 'HL', 'AAS'],
          correctAnswer: 2,
          solution: 'Right triangle with hypotenuse and leg is HL'
        },
        {
          id: 18,
          question: 'Triangles have all three sides: 5, 12, 13 and 5, 12, 13. Which theorem?',
          options: ['SSS', 'SAS', 'HL', 'All of these'],
          correctAnswer: 0,
          solution: 'All three sides match, so SSS (could also use HL since it\'s a right triangle)'
        },
        {
          id: 19,
          question: 'Triangles have angles 30°, 60°, 90° but different side lengths. Congruent?',
          options: ['Yes, by AAA', 'Yes, by ASA', 'No, different sizes', 'Cannot tell'],
          correctAnswer: 2,
          solution: 'Same angles but different sizes means similar, not congruent'
        },
        {
          id: 20,
          question: 'Which is NOT a valid congruence theorem?',
          options: ['SSS', 'AAA', 'SAS', 'ASA'],
          correctAnswer: 1,
          solution: 'AAA proves similarity only, not congruence'
        }
      ]
    }
  ]
};